import React, {useCallback, useEffect, useRef, useState} from 'react';
import {formatDate as formatApiDate, parseApiDate} from '../utils/dateTime';
import {useNavigate, useParams} from 'react-router-dom';
import createAxiosInstance from "../support/AxiosSupport";
import {pluralItems, pluralTimes} from '../utils/plural';
import {useToast} from '../context/ToastContext';
import {fetchRef} from "../service/refsCache";
import {useWardrobe} from "../context/WardrobeContext";
import WearStateControls, {CategoryCapability, WearState, toWearStatePayload} from "../components/WearStateControls";
import Spinner from "../components/Spinner";
import ConfirmDialog from "../components/ConfirmDialog";
import IconButton from "../components/IconButton";
import AuthImage, {openImageInNewWindow} from "../components/AuthImage";
import OutfitScoreBadge, {OutfitScoreDiagnostic} from "../components/OutfitScoreBadge";
import OutfitScoreDetails from "../components/OutfitScoreDetails";
import {SLOT_LABELS, SLOT_ORDER, slotLabel} from "../support/outfitLabels";
import {
    ArrowLeftIcon, CameraIcon, CheckCheckIcon, ClockIcon, LightbulbIcon, MaximizeIcon, PencilIcon,
    RefreshIcon, SparklesIcon, SunIcon, TrashIcon, XIcon,
} from "../components/icons";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface OutfitItem {
    id: string;
    name: string;
    brandName: string | null;
    layerName: string | null;
    rating: number | null;
    primaryPhotoUrl: string | null;
    slot: string | null;
    sortOrder: number | null;
    categoryId?: string | null;
    wearState?: WearState | null;
}

interface SavedOutfit {
    id: string;
    name: string;
    createdAt: string | null;
    items: OutfitItem[];
    wearCount: number;
    lastWornAt: string | null;
    favorite: boolean;
    primaryPhotoUrl: string | null;
}

interface OutfitPhoto {
    id: string;
    key: string;
    source: string;
    sortOrder: number;
    primary: boolean;
}

interface WhenToWear {
    seasons: string[] | null;
    occasions: string[] | null;
    tempMinC: number | null;
    tempMaxC: number | null;
}

interface Analytics {
    analyzed: boolean;
    stale: boolean;
    overall: number;
    label: string;
    color: number;
    style: number;
    seasonality: number;
    formality: number;
    comfort: number;
    whenToWear: WhenToWear | null;
    // outfit-score-engine (additive): единый OutfitScore движка с диагностикой
    outfitScore?: number | null;
    scoreLabel?: string | null;
    confidence?: number | null;
    diagnostics?: OutfitScoreDiagnostic[];
}

interface Insight {
    kind: string | null;
    icon: string | null;
    text: string;
}

interface Insights {
    insights: Insight[];
    stale: boolean;
}

interface Alternative {
    items: OutfitItem[];
    overall: number;
    label: string;
    changedSlot: string;
}

interface Replacement {
    itemId: string;
    name: string;
    brandName: string | null;
    primaryPhotoUrl: string | null;
    slot: string;
    score: number;
    matchReasons: string[] | null;
}

interface ImageStatus {
    status: string;
    photoId: string | null;
    error: string | null;
}

const RATING_OPTIONS: [number, string][] = [
    [5, 'Очень понравился'], [4, 'Понравился'], [3, 'Нормально'], [2, 'Не понравился'], [1, 'Ужасно'],
];
const FEEDBACK_REASONS = ['Было жарко', 'Было холодно', 'Неудобная обувь', 'Не понравилось сочетание', 'Получил комплименты'];

interface PendingFeedback {
    wearId: string;
    wornAt: string | null;
}

interface WearEntry {
    wearId: string;
    wornAt: string | null;
    occasion: string | null;
    weatherTempMinC: number | null;
    weatherTempMaxC: number | null;
    weatherCondition: string | null;
    rating: number | null;
    reasons: string[] | null;
    comment: string | null;
    photoId: string | null;
    photoKey: string | null;
}

interface WearSummary {
    wearCount: number;
    ratingsCount: number;
    avgRating: number | null;
    avgTempMinC: number | null;
    avgTempMaxC: number | null;
    topOccasions: string[] | null;
    bestSeason: string | null;
    bestTimeOfDay: string | null;
}

interface WearHistory {
    wears: WearEntry[];
    summary: WearSummary;
}

/** Ответ POST /outfits/saved/{id}/wear (WearOutfitResultDto). */
interface WearOutfitResult {
    wornCount?: number;
    wearId?: string;
    alreadyWornToday?: boolean;
}

const OCCASION_NAMES: Record<string, string> = {
    WORK: 'Офис', CORPORATE: 'Деловая встреча', DATE: 'Свидание', WALK: 'Прогулка',
    CAFE: 'Кафе', TRAVEL: 'Поездка', SPORT: 'Спорт', EVERYDAY: 'Повседневность',
    SHOPPING: 'Шопинг', HOME: 'Дома', THEATRE: 'Театр', WEDDING: 'Свадьба', REMOTE_WORK: 'Удалёнка',
};

const Stars: React.FC<{value: number; size?: string}> = ({value, size = 'text-base'}) => (
    <span className={`${size} text-amber-400 tracking-tight`} aria-label={`${value} из 5`}>
        {'★'.repeat(Math.round(value))}<span className="text-gray-300 dark:text-ink-600">{'★'.repeat(Math.max(0, 5 - Math.round(value)))}</span>
    </span>
);

const InteractiveStars: React.FC<{value: number | null; onRate: (n: number) => void}> = ({value, onRate}) => (
    <span className="inline-flex">
        {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => onRate(n)} aria-label={`Оценка ${n}`}
                    className={`text-lg leading-none px-0.5 hover:scale-110 transition-transform ${n <= (value ?? 0) ? 'text-amber-400' : 'text-gray-300 dark:text-ink-600'}`}>
                ★
            </button>
        ))}
    </span>
);

const formatDate = (iso: string | null): string =>
    formatApiDate(iso, {day: 'numeric', month: 'long', year: 'numeric'}, '');

function relativeDays(iso: string | null): string {
    const d = parseApiDate(iso);
    if (!d) return '—';
    const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (diff <= 0) return 'сегодня';
    if (diff === 1) return 'вчера';
    if (diff < 5) return `${diff} дня назад`;
    return `${diff} дней назад`;
}

const METRIC_LABELS: [keyof Analytics, string][] = [
    ['color', 'Цвета'], ['style', 'Стиль'], ['seasonality', 'Сезонность'],
    ['formality', 'Формальность'], ['comfort', 'Комфорт'],
];

// Светофор по оценке: ≥70 — зелёный, 40–69 — жёлтый, <40 — красный.
type ScoreTone = {stroke: string; bg: string; text: string; fill: string};
function scoreTone(v: number): ScoreTone {
    if (v >= 70) return {stroke: 'stroke-green-500', bg: 'bg-green-500', text: 'text-green-500', fill: 'fill-green-500'};
    if (v >= 40) return {stroke: 'stroke-amber-400', bg: 'bg-amber-400', text: 'text-amber-400', fill: 'fill-amber-400'};
    return {stroke: 'stroke-red-500', bg: 'bg-red-500', text: 'text-red-500', fill: 'fill-red-500'};
}

const CompatRing: React.FC<{value: number}> = ({value}) => {
    const r = 42;
    const c = 2 * Math.PI * r;
    const offset = c * (1 - Math.max(0, Math.min(100, value)) / 100);
    const tone = scoreTone(value);
    return (
        <svg viewBox="0 0 100 100" className="w-28 h-28">
            <circle cx="50" cy="50" r={r} className="stroke-gray-200 dark:stroke-ink-700" strokeWidth="8" fill="none"/>
            <circle
                cx="50" cy="50" r={r} className={tone.stroke} strokeWidth="8" fill="none"
                strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
                transform="rotate(-90 50 50)"
            />
            <text x="50" y="54" textAnchor="middle" className={`${tone.fill} font-bold`} fontSize="22">
                {value}%
            </text>
        </svg>
    );
};

const OutfitDetailPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const {showToast} = useToast();
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();

    const [outfit, setOutfit] = useState<SavedOutfit | null>(null);
    // Заголовок вкладки: до загрузки — имя раздела, после — название образа.
    useDocumentTitle(outfit?.name);
    const [photos, setPhotos] = useState<OutfitPhoto[]>([]);
    const [analytics, setAnalytics] = useState<Analytics | null>(null);
    const [insights, setInsights] = useState<Insights | null>(null);
    const [alternatives, setAlternatives] = useState<Alternative[]>([]);
    const [activeSlot, setActiveSlot] = useState<string | null>(null);
    const [replacements, setReplacements] = useState<Replacement[]>([]);
    const [replacementsLoading, setReplacementsLoading] = useState(false);
    const [activePhoto, setActivePhoto] = useState<number>(0);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [wearing, setWearing] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [generating, setGenerating] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [wearHistory, setWearHistory] = useState<WearHistory | null>(null);
    const [pendingFeedback, setPendingFeedback] = useState<PendingFeedback | null>(null);
    const [fbRating, setFbRating] = useState<number | null>(null);
    const [fbReasons, setFbReasons] = useState<string[]>([]);
    const [savingFeedback, setSavingFeedback] = useState(false);
    // wear-state: capabilities по категориям и статус сохранения состояний
    const [capabilities, setCapabilities] = useState<Record<string, CategoryCapability>>({});
    const [savingWearState, setSavingWearState] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);
    const wearPhotoInput = useRef<HTMLInputElement>(null);
    const wearPhotoTarget = useRef<string | null>(null);
    const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

    const canManage = isOwnWardrobe || (permissions?.includes('MANAGE_LISTS') ?? false);
    const canWear = isOwnWardrobe || (permissions?.includes('EDIT_ITEMS') ?? false);
    const ownerParam = selectedOwnerId ? {ownerId: selectedOwnerId} : {};

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<SavedOutfit>(`/outfits/saved/${id}`, {params: ownerParam});
            setOutfit(res.data);
            setNameDraft(res.data.name);

            const settle = <T,>(p: Promise<{data: T}>, set: (v: T) => void) =>
                p.then(r => set(r.data)).catch(() => undefined);
            await Promise.all([
                settle(api.get<OutfitPhoto[]>(`/outfits/saved/${id}/photos`, {params: ownerParam}), setPhotos),
                settle(api.get<Analytics>(`/outfits/saved/${id}/analytics`, {params: ownerParam}), setAnalytics),
                settle(api.get<Insights>(`/outfits/saved/${id}/insights`, {params: ownerParam}), setInsights),
                settle(api.get<Alternative[]>(`/outfits/saved/${id}/alternatives`, {params: ownerParam}), setAlternatives),
                settle(api.get<WearHistory>(`/outfits/saved/${id}/wears`, {params: ownerParam}), setWearHistory),
                // 204 → пусто; берём только если есть незакрытая носка
                api.get(`/outfits/saved/${id}/feedback/pending`, {params: ownerParam})
                    .then(r => setPendingFeedback(r.data && r.data.wearId ? r.data : null))
                    .catch(() => setPendingFeedback(null)),
            ]);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, selectedOwnerId]);

    useEffect(() => {
        load();
        return () => {
            if (pollTimer.current) clearInterval(pollTimer.current);
        };
    }, [load]);

    // wear-state: справочник capabilities — какие переключатели доступны вещам категорий.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const caps = await fetchRef<CategoryCapability[]>('category-capabilities');
                if (!cancelled) {
                    const byCategory: Record<string, CategoryCapability> = {};
                    for (const cap of caps) byCategory[cap.categoryId] = cap;
                    setCapabilities(byCategory);
                }
            } catch {
                /* без справочника переключатели состояний скрыты */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // wear-state: сохранение состояния вещи — PUT с полным составом (существующий контракт
    // update: переданный items полностью заменяет состав, поэтому шлём все элементы).
    const saveWearState = async (itemId: string, next: WearState | null) => {
        if (!outfit || savingWearState) return;
        const prevItems = outfit.items;
        const nextItems = outfit.items.map(it => it.id === itemId ? {...it, wearState: next} : it);
        setOutfit({...outfit, items: nextItems});
        setSavingWearState(true);
        try {
            const api = await createAxiosInstance();
            await api.put(`/outfits/saved/${outfit.id}`, {
                name: outfit.name,
                items: nextItems.map((it, idx) => ({
                    itemId: it.id,
                    slot: it.slot ?? undefined,
                    sortOrder: it.sortOrder ?? idx,
                    wearState: toWearStatePayload(it.wearState),
                })),
            }, {params: ownerParam});
        } catch {
            setOutfit(prev => prev ? {...prev, items: prevItems} : prev);
            setMsg('Не удалось сохранить состояние вещи');
        } finally {
            setSavingWearState(false);
        }
    };

    // Slots present in the outfit, ordered.
    const slots = outfit
        ? SLOT_ORDER.filter(s => outfit.items.some(i => i.slot === s))
        : [];

    useEffect(() => {
        if (slots.length > 0 && !activeSlot) setActiveSlot(slots[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outfit]);

    const loadReplacements = useCallback(async (slot: string) => {
        if (!id) return;
        setActiveSlot(slot);
        setReplacementsLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<Replacement[]>(`/outfits/saved/${id}/replacements`,
                {params: {...ownerParam, slot, limit: 8}});
            setReplacements(res.data);
        } catch {
            setReplacements([]);
        } finally {
            setReplacementsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, selectedOwnerId]);

    useEffect(() => {
        if (activeSlot) loadReplacements(activeSlot);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSlot]);

    // U16: после «Надеть образ» / отмены носки обновляем только затронутые данные
    // (сам образ, историю носок, аналитику, незакрытый фидбек) — без спиннера всей страницы.
    const refreshAfterWear = async (api: Awaited<ReturnType<typeof createAxiosInstance>>) => {
        if (!outfit) return;
        const settle = <T,>(p: Promise<{data: T}>, set: (v: T) => void) =>
            p.then(r => set(r.data)).catch(() => undefined);
        await Promise.all([
            settle(api.get<SavedOutfit>(`/outfits/saved/${outfit.id}`, {params: ownerParam}), setOutfit),
            settle(api.get<WearHistory>(`/outfits/saved/${outfit.id}/wears`, {params: ownerParam}), setWearHistory),
            settle(api.get<Analytics>(`/outfits/saved/${outfit.id}/analytics`, {params: ownerParam}), setAnalytics),
            api.get(`/outfits/saved/${outfit.id}/feedback/pending`, {params: ownerParam})
                .then(r => setPendingFeedback(r.data && r.data.wearId ? r.data : null))
                .catch(() => undefined),
        ]);
    };

    /** Отмена носки образа (QA-8): снимает запись и носки вещей, созданные вместе с ней. */
    const undoWear = async (wearId: string) => {
        if (!outfit) return;
        try {
            const api = await createAxiosInstance();
            await api.delete(`/outfits/saved/${outfit.id}/wears/${wearId}`, {params: ownerParam});
            showToast('Носка образа отменена', 'success');
            await refreshAfterWear(api);
        } catch {
            showToast('Не удалось отменить носку образа', 'error');
        }
    };

    // Одна носка образа в сутки: повторное «Надеть» не плодит запись, но отмену предлагает всегда.
    const wear = async () => {
        if (!outfit) return;
        setWearing(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<WearOutfitResult>(`/outfits/saved/${outfit.id}/wear`, {}, {params: ownerParam});
            const {wornCount, wearId, alreadyWornToday} = res.data ?? {};
            showToast(
                alreadyWornToday ? 'Уже отмечено сегодня' : `Образ отмечен как надетый (${pluralItems(wornCount ?? 0)})`,
                alreadyWornToday ? 'info' : 'success',
                wearId ? {label: 'Отменить', onClick: () => void undoWear(wearId)} : undefined,
            );
            await refreshAfterWear(api);
        } catch {
            showToast('Не удалось отметить образ', 'error');
        } finally {
            setWearing(false);
        }
    };

    const reloadHistory = async () => {
        if (!outfit) return;
        try {
            const api = await createAxiosInstance();
            const wr = await api.get<WearHistory>(`/outfits/saved/${outfit.id}/wears`, {params: ownerParam});
            setWearHistory(wr.data);
        } catch { /* подтянется при перезагрузке */ }
    };

    // Удобная оценка конкретной носки прямо в истории.
    const rateWearEntry = async (wearId: string, rating: number) => {
        if (!outfit) return;
        try {
            const api = await createAxiosInstance();
            await api.post(`/outfits/saved/${outfit.id}/wears/${wearId}/feedback`, {rating}, {params: ownerParam});
            reloadHistory();
        } catch {
            setMsg('Не удалось сохранить оценку');
        }
    };

    const triggerWearPhoto = (wearId: string) => {
        wearPhotoTarget.current = wearId;
        wearPhotoInput.current?.click();
    };

    // Фото образа за конкретный день: загрузка в галерею + привязка к носке.
    const uploadWearPhoto = async (file: File) => {
        const wearId = wearPhotoTarget.current;
        if (!outfit || !wearId) return;
        const form = new FormData();
        form.append('file', file);
        try {
            const api = await createAxiosInstance();
            const photo = await api.post<{id: string}>(`/outfits/saved/${outfit.id}/photos`, form,
                {params: ownerParam, headers: {'Content-Type': 'multipart/form-data'}});
            await api.post(`/outfits/saved/${outfit.id}/wears/${wearId}/photo`, {}, {params: {...ownerParam, photoId: photo.data.id}});
            const ph = await api.get<OutfitPhoto[]>(`/outfits/saved/${outfit.id}/photos`, {params: ownerParam});
            setPhotos(ph.data);
            reloadHistory();
        } catch {
            setMsg('Не удалось загрузить фото за день');
        }
    };

    const toggleReason = (rsn: string) =>
        setFbReasons(prev => prev.includes(rsn) ? prev.filter(r => r !== rsn) : [...prev, rsn]);

    const submitFeedback = async () => {
        if (!outfit || fbRating == null) return;
        setSavingFeedback(true);
        try {
            const api = await createAxiosInstance();
            await api.post(`/outfits/saved/${outfit.id}/feedback`,
                {rating: fbRating, reasons: fbReasons}, {params: ownerParam});
            setPendingFeedback(null);
            try {
                const wr = await api.get<WearHistory>(`/outfits/saved/${outfit.id}/wears`, {params: ownerParam});
                setWearHistory(wr.data);
            } catch { /* история подтянется при перезагрузке */ }
            setMsg('Спасибо! Фидбэк поможет AI подбирать точнее.');
        } catch {
            setMsg('Не удалось сохранить фидбэк');
        } finally {
            setSavingFeedback(false);
        }
    };

    const remove = async () => {
        if (!outfit) return;
        try {
            const api = await createAxiosInstance();
            await api.delete(`/outfits/saved/${outfit.id}`, {params: ownerParam});
            navigate('/outfits/saved');
        } catch {
            setMsg('Не удалось удалить образ');
            setConfirmDelete(false);
        }
    };

    const saveName = async () => {
        if (!outfit || !nameDraft.trim()) {
            setEditingName(false);
            return;
        }
        try {
            const api = await createAxiosInstance();
            await api.put(`/outfits/saved/${outfit.id}`, {name: nameDraft.trim()}, {params: ownerParam});
            setOutfit({...outfit, name: nameDraft.trim()});
        } catch {
            setMsg('Не удалось переименовать');
        } finally {
            setEditingName(false);
        }
    };

    const uploadPhoto = async (file: File) => {
        if (!outfit) return;
        const form = new FormData();
        form.append('file', file);
        try {
            const api = await createAxiosInstance();
            await api.post(`/outfits/saved/${outfit.id}/photos`, form,
                {params: ownerParam, headers: {'Content-Type': 'multipart/form-data'}});
            const res = await api.get<OutfitPhoto[]>(`/outfits/saved/${outfit.id}/photos`, {params: ownerParam});
            setPhotos(res.data);
        } catch {
            setMsg('Не удалось загрузить фото');
        }
    };

    const deletePhoto = async (photoId: string) => {
        if (!outfit) return;
        try {
            const api = await createAxiosInstance();
            await api.delete(`/outfits/saved/${outfit.id}/photos/${photoId}`, {params: ownerParam});
            const res = await api.get<OutfitPhoto[]>(`/outfits/saved/${outfit.id}/photos`, {params: ownerParam});
            setPhotos(res.data);
            setActivePhoto(0);
        } catch {
            setMsg('Не удалось удалить фото');
        }
    };

    const generateImage = async () => {
        if (!outfit) return;
        setGenerating(true);
        setMsg('Генерация фото образа запущена…');
        try {
            const api = await createAxiosInstance();
            await api.post(`/outfits/saved/${outfit.id}/generate-image`, {}, {params: ownerParam});
            pollTimer.current = setInterval(async () => {
                try {
                    const api2 = await createAxiosInstance();
                    const st = await api2.get<ImageStatus>(`/outfits/saved/${outfit.id}/generate-image/status`, {params: ownerParam});
                    if (st.data.status === 'COMPLETED' || st.data.status === 'FAILED') {
                        if (pollTimer.current) clearInterval(pollTimer.current);
                        setGenerating(false);
                        if (st.data.status === 'COMPLETED') {
                            const ph = await api2.get<OutfitPhoto[]>(`/outfits/saved/${outfit.id}/photos`, {params: ownerParam});
                            setPhotos(ph.data);
                            setMsg('Фото образа сгенерировано');
                        } else {
                            setMsg('Не удалось сгенерировать фото');
                        }
                    }
                } catch {
                    if (pollTimer.current) clearInterval(pollTimer.current);
                    setGenerating(false);
                }
            }, 3000);
        } catch (e) {
            setGenerating(false);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const code = (e as any)?.response?.status;
            setMsg(code === 402 ? 'Нужен AI-ключ для генерации' : 'Не удалось запустить генерацию');
        }
    };

    // AI-анализ образа по фото: заполняет аналитику И инсайды одним проходом, вручную.
    const runAnalysis = async () => {
        if (!outfit) return;
        setAnalyzing(true);
        setMsg('Анализируем образ по фото…');
        try {
            const api = await createAxiosInstance();
            const res = await api.post<Analytics>(`/outfits/saved/${outfit.id}/analytics/regenerate`, {}, {params: ownerParam});
            setAnalytics(res.data);
            try {
                const ins = await api.get<Insights>(`/outfits/saved/${outfit.id}/insights`, {params: ownerParam});
                setInsights(ins.data);
            } catch { /* инсайты подтянутся при следующей загрузке */ }
            setMsg('Образ оценён ИИ');
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const code = (e as any)?.response?.status;
            setMsg(
                code === 402 ? 'Нужен AI-ключ для анализа' :
                code === 403 ? 'Нужно согласие на профилирование по фото (в настройках)' :
                code === 400 ? 'У вещей образа нет фото для анализа' :
                'Не удалось проанализировать образ'
            );
        } finally {
            setAnalyzing(false);
        }
    };

    const openBuilderEdit = () => navigate(`/outfits/builder?outfitId=${id}`);

    // Точечная замена вещи прямо на странице образа (без ухода в конструктор): применяем
    // POST /replace, обновляем состав на месте и подтягиваем свежих кандидатов слота.
    const [replacing, setReplacing] = useState(false);
    const applyReplace = async (slot: string, newItemId: string) => {
        if (!outfit || replacing) return;
        setReplacing(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<SavedOutfit>(`/outfits/saved/${outfit.id}/replace`,
                {slot, newItemId}, {params: ownerParam});
            setOutfit(res.data);
            setMsg('Вещь заменена');
            loadReplacements(slot);
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const code = (e as any)?.response?.status;
            setMsg(
                code === 409 ? 'Эта вещь уже в образе' :
                code === 400 ? 'Вещь из другого слота' :
                code === 403 ? 'Вещь недоступна' :
                'Не удалось заменить вещь'
            );
        } finally {
            setReplacing(false);
        }
    };

    if (loading) return <Spinner/>;

    if (error || !outfit) {
        return (
            <div className="p-6 text-center space-y-3">
                <p className="text-gray-600 dark:text-gray-300">Образ не найден</p>
                <button onClick={() => navigate('/outfits/saved')}
                        className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700">
                    К каталогу образов
                </button>
            </div>
        );
    }

    const galleryPhotos = photos.length > 0 ? photos : [];
    const current = galleryPhotos[activePhoto] ?? galleryPhotos[0];

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                    <IconButton
                        label="Назад к каталогу образов"
                        title="Назад"
                        onClick={() => navigate('/outfits/saved')}
                        className="mt-1 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-ink-800 transition-colors shrink-0"
                    >
                        <ArrowLeftIcon className="w-5 h-5"/>
                    </IconButton>
                    <div className="min-w-0">
                        {editingName ? (
                            <input
                                autoFocus value={nameDraft}
                                onChange={e => setNameDraft(e.target.value)}
                                onBlur={saveName}
                                onKeyDown={e => e.key === 'Enter' && saveName()}
                                className="text-2xl font-bold bg-transparent border-b border-brand-400 outline-none text-gray-900 dark:text-gray-100"
                            />
                        ) : (
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                                {outfit.name}
                                {canManage && (
                                    <IconButton
                                        label="Переименовать образ"
                                        title="Переименовать"
                                        size="compact"
                                        onClick={() => setEditingName(true)}
                                        className="text-gray-300 hover:text-gray-600 dark:hover:text-gray-300"
                                    >
                                        <PencilIcon className="w-4 h-4"/>
                                    </IconButton>
                                )}
                            </h1>
                        )}
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">
                            Создан {formatDate(outfit.createdAt)} · {pluralItems(outfit.items.length)}
                        </p>
                        {wearHistory?.summary?.avgRating != null && (
                            <p className="text-sm mt-1 flex items-center gap-1.5">
                                <span className="font-semibold text-gray-900 dark:text-gray-100">{wearHistory.summary.avgRating}</span>
                                <Stars value={wearHistory.summary.avgRating} size="text-sm"/>
                                <span className="text-gray-400">({wearHistory.summary.ratingsCount} оценок)</span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canWear && (
                        <button onClick={wear} disabled={wearing}
                                className="px-5 py-2.5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60 text-sm font-medium transition-colors">
                            {wearing ? 'Отмечаем…' : 'Надеть образ'}
                        </button>
                    )}
                    {/* Подтверждение — общий `ConfirmDialog`, а не самодельное «Удалить? Да/Нет»
                        в строке действий: то не читалось как решение и не знало Escape (U13). */}
                    {canManage && (
                        <IconButton
                            label="Удалить образ"
                            title="Удалить"
                            onClick={() => setConfirmDelete(true)}
                            className="rounded-xl border border-gray-200 dark:border-ink-700 text-red-500 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            data-testid="outfit-delete"
                        >
                            <TrashIcon className="w-4 h-4"/>
                        </IconButton>
                    )}
                </div>
            </div>

            <ConfirmDialog
                open={confirmDelete}
                title="Удалить образ?"
                description="Образ исчезнет из каталога. Вещи из него останутся в гардеробе."
                confirmLabel="Удалить"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmDelete(false)}
                data-testid="outfit-delete-dialog"
            />

            {msg && (
                <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-ink-800 text-sm text-gray-700 dark:text-gray-300">
                    {msg}
                    <IconButton
                        label="Скрыть сообщение"
                        size="compact"
                        onClick={() => setMsg(null)}
                        className="ml-3 text-gray-400 hover:text-gray-600"
                    >
                        <XIcon className="w-4 h-4"/>
                    </IconButton>
                </div>
            )}

            {/* Как прошёл день? — пост-фидбэк после носки */}
            {canWear && pendingFeedback && (
                <div className="rounded-2xl border border-brand-200 dark:border-brand-800 bg-brand-50/40 dark:bg-brand-900/15 p-4 space-y-3">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Как прошёл день?</h3>
                    <div className="flex flex-wrap gap-2">
                        {RATING_OPTIONS.map(([val, label]) => (
                            <button key={val} onClick={() => setFbRating(val)}
                                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${fbRating === val
                                        ? 'bg-brand-600 text-white border-brand-600'
                                        : 'border-gray-200 dark:border-ink-700 text-gray-600 dark:text-gray-300 hover:border-brand-300'}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {FEEDBACK_REASONS.map(rsn => (
                            <button key={rsn} onClick={() => toggleReason(rsn)}
                                    className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${fbReasons.includes(rsn)
                                        ? 'bg-brand-50 dark:bg-brand-900/30 border-brand-400 text-brand-700 dark:text-brand-300'
                                        : 'border-gray-200 dark:border-ink-700 text-gray-500 dark:text-gray-400 hover:border-brand-300'}`}>
                                {rsn}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={submitFeedback} disabled={fbRating == null || savingFeedback}
                                className="px-4 py-2 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 text-sm font-medium">
                            {savingFeedback ? 'Сохраняем…' : 'Отправить'}
                        </button>
                        <button onClick={() => setPendingFeedback(null)}
                                className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            Позже
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Gallery */}
                <div className="space-y-4">
                    <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-gray-100 dark:bg-ink-800 relative group">
                        {current ? (
                            <>
                                <AuthImage imageKey={current.key} alt={outfit.name} variant="medium" expandable
                                           className="w-full h-full object-cover"/>
                                <IconButton
                                    label="Открыть фото образа в новом окне"
                                    title="Открыть в новом окне"
                                    size="compact"
                                    onClick={() => openImageInNewWindow(current.key)}
                                    className="absolute top-2 left-2 p-1.5 rounded-lg bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/75"
                                >
                                    <MaximizeIcon className="w-4 h-4"/>
                                </IconButton>
                            </>
                        ) : (
                            <div className="w-full h-full grid grid-cols-2 gap-px">
                                {outfit.items.slice(0, 4).map(it => (
                                    <div key={it.id} className="bg-gray-200 dark:bg-ink-700 overflow-hidden">
                                        {it.primaryPhotoUrl
                                            ? <AuthImage imageKey={it.primaryPhotoUrl} alt={it.name} variant="thumbnail" className="w-full h-full object-cover"/>
                                            : <div className="w-full h-full flex items-center justify-center"><SparklesIcon className="w-6 h-6 text-gray-300"/></div>}
                                    </div>
                                ))}
                            </div>
                        )}
                        {galleryPhotos.length > 0 && (
                            <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-white text-xs">
                                {activePhoto + 1}/{galleryPhotos.length}
                            </span>
                        )}
                    </div>
                    {galleryPhotos.length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                            {galleryPhotos.map((p, idx) => (
                                <button key={p.id} onClick={() => setActivePhoto(idx)}
                                        className={`relative w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${idx === activePhoto ? 'border-brand-500' : 'border-transparent'}`}>
                                    <AuthImage imageKey={p.key} alt="" variant="thumbnail" className="w-full h-full object-cover"/>
                                    {canManage && (
                                        <span onClick={(e) => {e.stopPropagation(); deletePhoto(p.id);}}
                                              className="absolute top-0 right-0 bg-black/60 text-white p-0.5 rounded-bl"><XIcon className="w-3 h-3"/></span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                    {canManage && (
                        <div className="flex gap-2">
                            <input ref={fileInput} type="file" accept="image/*" className="hidden"
                                   onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])}/>
                            <button onClick={() => fileInput.current?.click()}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 dark:border-ink-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-ink-800">
                                <CameraIcon className="w-4 h-4"/> Загрузить
                            </button>
                            <button onClick={generateImage} disabled={generating}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-brand-200 dark:border-brand-800 text-sm text-brand-600 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20 disabled:opacity-60">
                                <SparklesIcon className="w-4 h-4"/> {generating ? 'Генерация…' : 'Сгенерировать'}
                            </button>
                        </div>
                    )}

                    {/* When to wear */}
                    {analytics?.whenToWear && (
                        <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-2">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Когда носить</h3>
                            <div className="flex flex-wrap gap-2">
                                {(analytics.whenToWear.seasons ?? []).map(s => <Chip key={s} text={s}/>)}
                                {analytics.whenToWear.tempMinC != null && (
                                    <Chip text={`${analytics.whenToWear.tempMinC}…${analytics.whenToWear.tempMaxC}°C`}/>
                                )}
                                {(analytics.whenToWear.occasions ?? []).map(o => <Chip key={o} text={o}/>)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Composition */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Состав образа</h2>
                        {canManage && (
                            <button onClick={openBuilderEdit}
                                    className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700">
                                <PencilIcon className="w-4 h-4"/> Изменить состав
                            </button>
                        )}
                    </div>
                    <div className="space-y-2">
                        {outfit.items.map(item => (
                            <div key={item.id}
                                 className="p-3 rounded-xl border border-gray-200 dark:border-ink-700 bg-white dark:bg-ink-900 space-y-2">
                                <div className="flex items-center gap-3">
                                    <div onClick={() => navigate(`/clothing/${item.id}`)}
                                         className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-ink-800 shrink-0 cursor-pointer">
                                        {item.primaryPhotoUrl
                                            ? <AuthImage imageKey={item.primaryPhotoUrl} alt={item.name} variant="thumbnail" className="w-full h-full object-cover"/>
                                            : <div className="w-full h-full flex items-center justify-center"><SparklesIcon className="w-5 h-5 text-gray-300"/></div>}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                            {[item.brandName, slotLabel(item.slot, item.layerName)].filter(Boolean).join(' · ')}
                                        </p>
                                    </div>
                                    {canManage && item.slot && (
                                        <IconButton
                                            label={`Заменить «${item.name}»`}
                                            title="Заменить"
                                            onClick={() => setActiveSlot(item.slot as string)}
                                            className="rounded-lg text-gray-400 hover:text-brand-600 hover:bg-gray-50 dark:hover:bg-ink-800"
                                        >
                                            <RefreshIcon className="w-4 h-4"/>
                                        </IconButton>
                                    )}
                                </div>
                                {/* wear-state: переключатели доступных состояний по capabilities категории */}
                                {canManage && item.categoryId && capabilities[item.categoryId] && (
                                    <WearStateControls
                                        capability={capabilities[item.categoryId]}
                                        value={item.wearState ?? null}
                                        onChange={next => saveWearState(item.id, next)}
                                    />
                                )}
                            </div>
                        ))}
                        {canManage && (
                            <button onClick={openBuilderEdit}
                                    className="w-full py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-ink-700 text-sm text-gray-500 hover:border-brand-300 hover:text-brand-600">
                                + Добавить вещь
                            </button>
                        )}
                    </div>
                </div>

                {/* Analytics + insights */}
                <div className="space-y-4">
                    <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Аналитика образа</h3>
                            {canManage && (
                                <button onClick={runAnalysis} disabled={analyzing}
                                        className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 disabled:opacity-50">
                                    <SparklesIcon className={`w-4 h-4 ${analyzing ? 'animate-pulse' : ''}`}/>
                                    {analyzing ? 'Анализируем…' : analytics?.analyzed ? 'Переоценить' : 'Анализировать'}
                                </button>
                            )}
                        </div>
                        {/* outfit-score-engine: детерминированный OutfitScore считается всегда,
                            даже до AI-анализа фото (числа — от движка, LLM — только тексты) */}
                        {analytics && (
                            <div className="mb-3 space-y-1.5">
                                <OutfitScoreBadge score={analytics.outfitScore} label={analytics.scoreLabel}/>
                                <OutfitScoreDetails diagnostics={analytics.diagnostics ?? []}/>
                            </div>
                        )}
                        {analytics?.analyzed ? (
                            <>
                                <div className="flex items-center gap-4">
                                    <CompatRing value={analytics.overall}/>
                                    <div className="flex-1 space-y-1.5">
                                        {METRIC_LABELS.map(([key, label]) => (
                                            <MetricBar key={key} label={label} value={analytics[key] as number}/>
                                        ))}
                                    </div>
                                </div>
                                <p className={`text-center text-sm font-medium mt-1 ${scoreTone(analytics.overall).text}`}>{analytics.label}</p>
                                {analytics.stale && (
                                    <p className="text-center text-xs text-amber-500 mt-1">Состав изменился — стоит переоценить</p>
                                )}
                                <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                                    <Stat icon={<CheckCheckIcon className="w-4 h-4"/>} label="Надевался" value={pluralTimes(outfit.wearCount)}/>
                                    <Stat icon={<ClockIcon className="w-4 h-4"/>} label="Последний раз" value={relativeDays(outfit.lastWornAt)}/>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-6 space-y-2">
                                <SparklesIcon className="w-8 h-8 mx-auto text-gray-300 dark:text-ink-600"/>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Образ ещё не оценён ИИ</p>
                                <p className="text-xs text-gray-400">
                                    {canManage ? 'Нажмите «Анализировать» — ИИ оценит образ по фото вещей' : 'Анализ доступен владельцу гардероба'}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                            <LightbulbIcon className="w-4 h-4 text-amber-400"/> Инсайды
                        </h3>
                        {insights && insights.insights.length > 0 ? (
                            <ul className="space-y-2.5">
                                {insights.insights.map((ins, i) => (
                                    <li key={i} className="flex gap-2 text-sm text-gray-600 dark:text-gray-300">
                                        <SparklesIcon className="w-4 h-4 text-brand-400 shrink-0 mt-0.5"/>
                                        <span>{ins.text}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-400">
                                {canManage ? 'Запустите «Анализировать образ» — инсайды появятся вместе с оценкой' : 'Инсайтов пока нет'}
                            </p>
                        )}
                        {insights?.stale && insights.insights.length > 0 && (
                            <p className="text-xs text-amber-500">Состав изменился — стоит переоценить</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Replace by slot */}
            {slots.length > 0 && (
                <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Попробуй заменить</h3>
                    <div className="flex gap-2 flex-wrap border-b border-gray-100 dark:border-ink-800 pb-2">
                        {slots.map(s => (
                            <button key={s} onClick={() => setActiveSlot(s)}
                                    className={`px-3 py-1.5 rounded-lg text-sm ${activeSlot === s ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-medium' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-ink-800'}`}>
                                {SLOT_LABELS[s]}
                            </button>
                        ))}
                    </div>
                    {replacementsLoading ? (
                        <p className="text-sm text-gray-400 py-4">Подбираем…</p>
                    ) : replacements.length === 0 ? (
                        <p className="text-sm text-gray-400 py-4">Нет подходящих вариантов</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {replacements.map(r => (
                                <button key={r.itemId} disabled={replacing}
                                        onClick={() => activeSlot && (canManage
                                            ? applyReplace(activeSlot, r.itemId)
                                            : navigate(`/clothing/${r.itemId}`))}
                                        title={canManage ? 'Заменить на эту вещь' : 'Открыть карточку вещи'}
                                        className="text-left rounded-xl border border-gray-200 dark:border-ink-700 overflow-hidden hover:shadow-md hover:border-brand-400 transition-[border-color,box-shadow] duration-ui ease-out disabled:opacity-50 group">
                                    <div className="aspect-square bg-gray-100 dark:bg-ink-800 relative">
                                        {r.primaryPhotoUrl
                                            ? <AuthImage imageKey={r.primaryPhotoUrl} alt={r.name} variant="thumbnail" className="w-full h-full object-cover"/>
                                            : <div className="w-full h-full flex items-center justify-center"><SparklesIcon className="w-6 h-6 text-gray-300"/></div>}
                                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-brand-600 text-white text-xs font-medium">{r.score}%</span>
                                        {canManage && (
                                            <span className="absolute inset-0 flex items-center justify-center bg-brand-600/0 group-hover:bg-brand-600/20 transition-colors opacity-0 group-hover:opacity-100">
                                                <RefreshIcon className="w-6 h-6 text-white drop-shadow"/>
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-2">
                                        <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{r.name}</p>
                                        {r.brandName && <p className="text-[11px] text-gray-400 truncate">{r.brandName}</p>}
                                        {(r.matchReasons ?? []).length > 0 && (
                                            <p className="text-[11px] text-gray-400 truncate">{(r.matchReasons ?? []).join(' · ')}</p>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Alternatives */}
            {alternatives.length > 0 && (
                <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Альтернативы</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {alternatives.map((alt, i) => (
                            <div key={i} className="rounded-xl border border-gray-200 dark:border-ink-700 p-3">
                                <div className="flex gap-1.5 mb-2">
                                    {alt.items.slice(0, 5).map(it => (
                                        <div key={it.id} className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-ink-800">
                                            {it.primaryPhotoUrl
                                                ? <AuthImage imageKey={it.primaryPhotoUrl} alt={it.name} variant="thumbnail" className="w-full h-full object-cover"/>
                                                : <div className="w-full h-full flex items-center justify-center"><SparklesIcon className="w-4 h-4 text-gray-300"/></div>}
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{alt.label}</span>
                                    <span className="text-sm text-brand-600 font-medium">{alt.overall}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* История надетых образов */}
            {wearHistory?.wears && wearHistory.wears.length > 0 && (
                <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">История надетых образов</h3>
                        <p className="text-xs text-gray-400">Каждое использование помогает AI подбирать ещё лучше</p>
                    </div>
                    <input ref={wearPhotoInput} type="file" accept="image/*" className="hidden"
                           onChange={e => { if (e.target.files?.[0]) { uploadWearPhoto(e.target.files[0]); e.target.value = ''; } }}/>
                    <div className="divide-y divide-gray-100 dark:divide-ink-800">
                        {wearHistory.wears.map(w => (
                            <div key={w.wearId} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                                <div className="sm:w-24 shrink-0 text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {formatApiDate(w.wornAt, {day: 'numeric', month: 'long'}, '')}
                                </div>
                                <div className="flex-1 min-w-0 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                                    {(w.weatherTempMinC != null || w.weatherTempMaxC != null) && (
                                        <p className="flex items-center gap-1.5"><SunIcon className="w-3.5 h-3.5 text-amber-400"/>
                                            {w.weatherCondition ? `${w.weatherCondition}, ` : ''}{w.weatherTempMinC ?? '?'}° / {w.weatherTempMaxC ?? '?'}°</p>
                                    )}
                                    {w.occasion && <p>{OCCASION_NAMES[w.occasion] ?? w.occasion}</p>}
                                </div>
                                {/* Удобная оценка прямо в строке */}
                                <div className="sm:w-52 shrink-0">
                                    {canWear
                                        ? <InteractiveStars value={w.rating} onRate={n => rateWearEntry(w.wearId, n)}/>
                                        : w.rating != null && <Stars value={w.rating} size="text-sm"/>}
                                    {w.comment && <p className="text-xs text-gray-400 mt-0.5">{w.comment}</p>}
                                    {(w.reasons ?? []).length > 0 && (
                                        <p className="text-xs text-gray-400 mt-0.5">{(w.reasons ?? []).join(' · ')}</p>
                                    )}
                                </div>
                                {/* Фото за этот день */}
                                <div className="sm:w-28 shrink-0">
                                    {w.photoKey ? (
                                        <button onClick={() => w.photoKey && openImageInNewWindow(w.photoKey)}
                                                title="Открыть фото в новом окне"
                                                aria-label="Открыть фото за день в новом окне"
                                                className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-ink-800 group/wp block">
                                            <AuthImage imageKey={w.photoKey} alt="Фото за день" variant="thumbnail" className="w-full h-full object-cover cursor-zoom-in"/>
                                            <span className="absolute inset-0 bg-black/0 group-hover/wp:bg-black/30 flex items-center justify-center opacity-0 group-hover/wp:opacity-100 transition-[opacity,background-color] duration-ui ease-out">
                                                <MaximizeIcon className="w-4 h-4 text-white"/>
                                            </span>
                                        </button>
                                    ) : canWear ? (
                                        <button onClick={() => triggerWearPhoto(w.wearId)}
                                                className="flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700">
                                            <CameraIcon className="w-4 h-4"/> Фото за день
                                        </button>
                                    ) : (
                                        <span className="text-xs text-gray-400">Нет фото</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Итоги по образу */}
            {wearHistory?.summary && wearHistory.summary.wearCount > 0 && (
                <div className="rounded-2xl border border-gray-200 dark:border-ink-700 p-4 space-y-3">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Итоги по образу</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-sm">
                        {wearHistory.summary.bestSeason && (
                            <SummaryCard label="Лучший сезон" value={wearHistory.summary.bestSeason} hint="Носишь чаще всего"/>
                        )}
                        {wearHistory.summary.bestTimeOfDay && (
                            <SummaryCard label="Лучшее время дня" value={wearHistory.summary.bestTimeOfDay} hint="Оценки выше"/>
                        )}
                        {(wearHistory.summary.avgTempMinC != null || wearHistory.summary.avgTempMaxC != null) && (
                            <SummaryCard label="Средняя температура" value={`${wearHistory.summary.avgTempMinC ?? '?'}°–${wearHistory.summary.avgTempMaxC ?? '?'}°`} hint="Оптимальный диапазон"/>
                        )}
                        {(wearHistory.summary.topOccasions ?? []).length > 0 && (
                            <SummaryCard label="Частые сценарии" value={(wearHistory.summary.topOccasions ?? []).map(o => OCCASION_NAMES[o] ?? o).join(', ')} hint=""/>
                        )}
                        {wearHistory.summary.avgRating != null && (
                            <SummaryCard label="Рейтинг комфорта" value={`${wearHistory.summary.avgRating} / 5`} hint={`На основе ${wearHistory.summary.ratingsCount} оценок`}/>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const SummaryCard: React.FC<{label: string; value: string; hint: string}> = ({label, value, hint}) => (
    <div className="rounded-xl border border-gray-200 dark:border-ink-700 p-3">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{value}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
);

const Chip: React.FC<{text: string}> = ({text}) => (
    <span className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-ink-800 text-xs text-gray-600 dark:text-gray-300">{text}</span>
);

const MetricBar: React.FC<{label: string; value: number}> = ({label, value}) => (
    <div className="flex items-center gap-2 text-xs">
        <span className="w-24 text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
        <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-ink-700 overflow-hidden">
            <div className={`h-full rounded-full ${scoreTone(value).bg}`} style={{width: `${value}%`}}/>
        </div>
        <span className="w-12 text-right text-gray-500 dark:text-gray-400 tabular-nums">{value}/100</span>
    </div>
);

const Stat: React.FC<{icon: React.ReactNode; label: string; value: string}> = ({icon, label, value}) => (
    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <span className="text-gray-400">{icon}</span>
        <span className="text-gray-400">{label}:</span>
        <span className="text-gray-700 dark:text-gray-200 font-medium ml-auto">{value}</span>
    </div>
);

export default OutfitDetailPage;
